import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, RefreshCw, ChevronLeft, ChevronRight, Activity, Star  } from 'lucide-react';
import { useFontSize } from './FontSizeContext';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { swalTheme } from '../lib/swalTheme';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next';
import StatusBadge from './StatusBadge';
import { useFavoriteFilters } from './useFavoriteFilters';

const SLAList = () => {
    const navigate = useNavigate();
    const { fontSize } = useFontSize();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [showAllSites, setShowAllSites] = useState(false);
    const { canCreate, canUpdate, canDelete } = usePermissions('sla');
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';

    const { saveFavorite } = useFavoriteFilters('SLA');
    const handleSaveFavoriteFilter = async () => {
        const { value: name } = await Swal.fire({
            ...swalTheme(),
            title: t('favorites.namePrompt', 'Name this favorite filter'),
            input: 'text',
            inputPlaceholder: t('favorites.namePlaceholder', 'e.g. My Open WOs'),
            showCancelButton: true,
            confirmButtonText: t('common.save', 'Save'),
            cancelButtonText: t('common.cancel', 'Cancel'),
            inputValidator: (value) => !value?.trim() && t('favorites.nameRequired', 'Please enter a name')
        });
        if (name && name.trim()) {
            saveFavorite(name, { searchTerm, all_sites: showAllSites });
        }
    };

    useEffect(() => {
        const applyFavorite = (e) => {
            if (e.detail?.appName === 'SLA' && e.detail?.filters) {
                const { searchTerm: favSearchTerm, all_sites: favAllSites } = e.detail.filters;
                if (favSearchTerm !== undefined) setSearchTerm(favSearchTerm);
                if (favAllSites !== undefined) setShowAllSites(favAllSites);
                setPage(1);
            }
        };
        window.addEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
        return () => window.removeEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
    }, []);

    const fetchSLA = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/sla`, {
                params: { page, q: searchTerm, all_sites: Boolean(showAllSites) },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setData(res.data.data);
            setTotal(res.data.total);
        } finally { setLoading(false); }
    }, [page, searchTerm, showAllSites]);

    useEffect(() => { fetchSLA(); }, [fetchSLA]);

    useEffect(() => {
        const openAllSites = () => { setShowAllSites(true); setPage(1); };
        const viewMySite = () => { setShowAllSites(false); setPage(1); };
        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        };
    }, []);

    return (
        <div className={`flex flex-col h-full bg-surface-app font-black uppercase tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }}>
            <div className="bg-surface-card p-4 border-b shadow-sm flex justify-between items-center px-8">
                <div className="flex items-center gap-6">
                    <h2 className="text-primary-dark text-xl italic flex items-center gap-2">
                        <Activity size={24} className="text-status-warning"/> {t('sla.list.title')}
                        <span className={`text-[0.69em] px-2 py-0.5 rounded-full uppercase font-bold border ml-2 ${showAllSites ? 'bg-status-danger-bg text-status-danger border-status-danger/30' : 'bg-primary-soft text-primary border-primary/30'}`}>
                            {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
                        </span>
                    </h2>
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-ink-secondary" size={16}/>
                        <input className="pl-10 pr-4 py-2 border w-[400px] outline-none focus:border-primary rounded-sm shadow-inner"
                               placeholder={t('sla.list.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <button
                        onClick={handleSaveFavoriteFilter}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-control border border-line bg-surface-panel text-status-warning text-[0.77em] font-bold uppercase hover:bg-primary-soft transition-all"
                    >
                        <Star size={13} /> {t('favorites.save', 'SAVE AS FAVORITE')}
                    </button>
                </div>
                {canCreate && (
                <button onClick={() => navigate('/sla/new')} className="bg-primary-dark text-white px-8 py-2.5 rounded-sm flex items-center gap-2 hover:bg-black transition-all shadow-lg">
                    <Plus size={18}/> {t('sla.list.new')}
                </button>
                )}
            </div>

            <div className="flex-1 p-6 overflow-auto">
                <div className="bg-surface-card border-2 border-line rounded shadow-2xl overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-primary-dark text-white sticky top-0 z-10">
                            <tr>
                                <th className="p-4 border-r border-white/10">{t('sla.header.slanum')}</th>
                                <th className="p-4 border-r border-white/10">{t('wo.fields.description')}</th>
                                <th className="p-4 border-r border-white/10">{t('sla.header.type')}</th>
                                <th className="p-4 border-r border-white/10">{t('sla.header.ranking')}</th>
                                <th className="p-4 text-center">{t('wo.headers.status')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-line-subtle">
                            {loading ? (
                                <tr><td colSpan="5" className="p-20 text-center animate-pulse">{t('sla.list.syncing')}</td></tr>
                            ) : data.map((s, i) => (
                                <tr key={i} onClick={() => navigate(`/sla/${s.slanum}?${new URLSearchParams({ q: searchTerm, all_sites: Boolean(showAllSites) }).toString()}`)} className="hover:bg-primary-soft cursor-pointer transition-all group">
                                    <td className="p-4 border-r font-black text-primary group-hover:underline">{s.slanum}</td>
                                    <td className="p-4 border-r text-ink italic">{s.description}</td>
                                    <td className="p-4 border-r text-center">{s.sla_type}</td>
                                    <td className="p-4 border-r text-center font-mono text-status-warning">{s.ranking}</td>
                                    <td className="p-4 text-center">
                                        <StatusBadge status={s.status} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-primary-dark text-white p-3 px-10 flex justify-between items-center shadow-inner">
                <span className="text-[0.77em]">{t('sla.list.total')}: <span className="text-status-warning ml-2">{total}</span></span>
                <div className="flex gap-2">
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-2 border border-white/20 rounded-sm hover:bg-status-warning disabled:opacity-20"><ChevronLeft size={18}/></button>
                    <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} className="p-2 border border-white/20 rounded-sm hover:bg-status-warning disabled:opacity-20"><ChevronRight size={18}/></button>
                </div>
            </div>
        </div>
    );
};

export default SLAList;
